'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ActivityUpdateMessage,
  AnyWebSocketMessage,
  BucketCloseMessage,
  ErrorMessage,
  OrderBookDataMessage,
  OrderPlacementResultMessage,
  OrderUpdateMessage,
  PricesDataDeltaMessage,
  PricesDataMessage,
  SubscribedMessage,
  TpSlUpdateMessage,
  UnsubscribedMessage,
} from '@/lib/types/websocket';

type ReadyState = WebSocket['readyState'];

export type UseWebSocketOptions = {
  url: string | undefined;
  autoConnect?: boolean;
  reconnect?: boolean;
  maxRetries?: number;
  baseDelayMs?: number; // default 1000ms
  maxDelayMs?: number; // default 30000ms
  /** Every incoming frame, before parsing (incl. non-JSON pongs). Kept out of
   *  React state on purpose: live feeds push many frames per second, and a
   *  per-frame setState would re-render the socket's host on each one. */
  onRawMessage?: (evt: MessageEvent) => void;
  // Optional typed handlers for incoming messages
  onMessage?: (msg: AnyWebSocketMessage) => void;
  onSubscribed?: (msg: SubscribedMessage) => void;
  onUnsubscribed?: (msg: UnsubscribedMessage) => void;
  onOrderBookData?: (
    msg: OrderBookDataMessage | PricesDataMessage | PricesDataDeltaMessage,
  ) => void;
  onChartsData?: (msg: BucketCloseMessage) => void;
  onOrderPlacementResult?: (msg: OrderPlacementResultMessage) => void;
  onOrderUpdate?: (msg: OrderUpdateMessage) => void;
  onActivityUpdate?: (msg: ActivityUpdateMessage) => void;
  onTpSlUpdate?: (msg: TpSlUpdateMessage) => void;
  onErrorMessage?: (msg: ErrorMessage) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
};

export function useWebSocket({
  url,
  autoConnect = true,
  reconnect = true,
  maxRetries,
  baseDelayMs = 1000,
  maxDelayMs = 30000,
  onRawMessage,
  onMessage,
  onSubscribed,
  onUnsubscribed,
  onOrderBookData,
  onChartsData,
  onOrderPlacementResult,
  onOrderUpdate,
  onActivityUpdate,
  onTpSlUpdate,
  onErrorMessage,
  onOpen,
  onClose,
  onError,
}: UseWebSocketOptions) {
  const socketRef = useRef<WebSocket | null>(null);
  const [readyState, setReadyState] = useState<ReadyState>(WebSocket.CLOSED);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  // Keep latest handlers in refs to avoid re-creating connect() on each render
  const onRawMessageRef = useRef<typeof onRawMessage | undefined>(undefined);
  const onMessageRef = useRef<typeof onMessage | undefined>(undefined);
  const onSubscribedRef = useRef<typeof onSubscribed | undefined>(undefined);
  const onUnsubscribedRef = useRef<typeof onUnsubscribed | undefined>(undefined);
  const onOrderBookDataRef = useRef<typeof onOrderBookData | undefined>(undefined);
  const onChartsDataRef = useRef<typeof onChartsData | undefined>(undefined);
  const onOrderPlacementResultRef = useRef<typeof onOrderPlacementResult | undefined>(undefined);
  const onOrderUpdateRef = useRef<typeof onOrderUpdate | undefined>(undefined);
  const onActivityUpdateRef = useRef<typeof onActivityUpdate | undefined>(undefined);
  const onTpSlUpdateRef = useRef<typeof onTpSlUpdate | undefined>(undefined);
  const onErrorMessageRef = useRef<typeof onErrorMessage | undefined>(undefined);
  const onOpenRef = useRef<typeof onOpen | undefined>(undefined);
  const onCloseRef = useRef<typeof onClose | undefined>(undefined);
  const onErrorRef = useRef<typeof onError | undefined>(undefined);

  useEffect(() => {
    onRawMessageRef.current = onRawMessage;
    onMessageRef.current = onMessage;
    onSubscribedRef.current = onSubscribed;
    onUnsubscribedRef.current = onUnsubscribed;
    onOrderBookDataRef.current = onOrderBookData;
    onChartsDataRef.current = onChartsData;
    onOrderPlacementResultRef.current = onOrderPlacementResult;
    onOrderUpdateRef.current = onOrderUpdate;
    onActivityUpdateRef.current = onActivityUpdate;
    onTpSlUpdateRef.current = onTpSlUpdate;
    onErrorMessageRef.current = onErrorMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
  }, [
    onRawMessage,
    onMessage,
    onSubscribed,
    onUnsubscribed,
    onOrderBookData,
    onChartsData,
    onOrderPlacementResult,
    onOrderUpdate,
    onActivityUpdate,
    onTpSlUpdate,
    onErrorMessage,
    onOpen,
    onClose,
    onError,
  ]);

  const connect = useCallback(() => {
    if (!url) return;
    if (
      socketRef.current &&
      (socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    shouldReconnectRef.current = true;

    const ws = new WebSocket(url);
    socketRef.current = ws;
    setReadyState(ws.readyState);

    const clearTimer = () => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const schedule = () => {
      if (!reconnect || !autoConnect || !url || !shouldReconnectRef.current) return;
      if (typeof maxRetries === 'number' && reconnectAttemptsRef.current >= maxRetries) return;
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      const exp = Math.min(baseDelayMs * Math.pow(2, attempt - 1), maxDelayMs);
      const jitter = Math.floor(Math.random() * Math.floor(exp * 0.2));
      const delay = exp + jitter;
      clearTimer();
      reconnectTimerRef.current = window.setTimeout(() => {
        if (!shouldReconnectRef.current) return;
        connect();
      }, delay);
    };

    const handleOpen = () => {
      setReadyState(ws.readyState);
      reconnectAttemptsRef.current = 0;
      clearTimer();
      onOpenRef.current?.();
    };
    const handleClose = (evt: CloseEvent) => {
      setReadyState(ws.readyState);
      onCloseRef.current?.(evt);
      // attempt to reconnect on unexpected close
      schedule();
    };
    const handleError = (evt: Event) => {
      setReadyState(ws.readyState);
      onErrorRef.current?.(evt);
    };
    const handleMessage = (evt: MessageEvent) => {
      onRawMessageRef.current?.(evt);
      let parsed: AnyWebSocketMessage | null = null;
      if (typeof evt.data === 'string') {
        try {
          parsed = JSON.parse(evt.data) as AnyWebSocketMessage;
        } catch {
          parsed = null;
        }
      }
      if (parsed) {
        // Always call onMessage handler first - it handles all message types including
        // unknown ones like BALANCE_UPDATE, TRANSACTION_UPDATE, etc.
        onMessageRef.current?.(parsed);
        // Then call specific typed handlers for known message types
        switch (parsed.t) {
          case 'SUBSCRIBED':
            onSubscribedRef.current?.(parsed);
            break;
          case 'UNSUBSCRIBED':
            onUnsubscribedRef.current?.(parsed);
            break;
          case 'PRICES_DATA':
          case 'PRICES_DATA_DELTA':
          case 'ORDER_BOOK_DATA':
            onOrderBookDataRef.current?.(parsed);
            break;
          case 'ORDER_BOOK_DATA_DELTA':
            // ORDER_BOOK_DATA_DELTA goes to onMessage handler (not onOrderBookData)
            break;
          case 'BUCKET_CLOSE':
            onChartsDataRef.current?.(parsed);
            break;
          case 'ORDER_PLACEMENT_RESULT':
            onOrderPlacementResultRef.current?.(parsed);
            break;
          case 'ORDER_UPDATE':
            onOrderUpdateRef.current?.(parsed);
            break;
          case 'ACTIVITY_UPDATE':
            onActivityUpdateRef.current?.(parsed);
            break;
          case 'TPSL_UPDATE':
            onTpSlUpdateRef.current?.(parsed);
            break;
          case 'ERROR':
            onErrorMessageRef.current?.(parsed);
            break;
          default:
            // Unknown message types (like BALANCE_UPDATE, TRANSACTION_UPDATE) are already
            // handled by onMessage handler above, so no additional action needed here
            break;
        }
      }
      // Non-JSON frames (heartbeat pongs, binary) reach onRawMessage above only.
    };

    ws.addEventListener('open', handleOpen);
    ws.addEventListener('close', handleClose);
    ws.addEventListener('error', handleError);
    ws.addEventListener('message', handleMessage);

    // detach listeners on manual reconnects happens in disconnect()
  }, [url, reconnect, autoConnect, maxRetries, baseDelayMs, maxDelayMs]);

  const disconnect = useCallback(() => {
    const s = socketRef.current;
    if (!s) return;
    shouldReconnectRef.current = false;
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (s.readyState === WebSocket.OPEN || s.readyState === WebSocket.CONNECTING) {
      s.close();
    }
    socketRef.current = null;
  }, []);

  const send = useCallback(
    (data: string | ArrayBufferLike | Blob | ArrayBufferView | AnyWebSocketMessage) => {
      const s = socketRef.current;
      if (!s || s.readyState !== WebSocket.OPEN) return false;
      const payload =
        typeof data === 'string' ||
        data instanceof ArrayBuffer ||
        ArrayBuffer.isView(data) ||
        data instanceof Blob
          ? data
          : JSON.stringify(data);
      // WebSocket.send accepts string or binary; we aligned payload accordingly
      s.send(payload as string);
      return true;
    },
    [],
  );

  useEffect(() => {
    if (!autoConnect || !url) return;
    connect();
    return () => disconnect();
    // Intentionally exclude connect/disconnect to avoid re-connect loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, url, disconnect, connect]);

  // Reconnect when connection is closed and the browser comes back online.
  // Same gate as the close handler: a socket that is off (`autoConnect: false`,
  // e.g. signed out or an ended event) must not open just because the network
  // came back.
  useEffect(() => {
    const handleOnline = () => {
      if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
        if (autoConnect && shouldReconnectRef.current) {
          connect();
        }
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [autoConnect, connect]);

  return {
    socket: socketRef.current,
    readyState,
    reconnectAttempts: reconnectAttemptsRef.current,
    connect,
    disconnect,
    send,
  } as const;
}
